/**
 * CONVERSACIONES — la bandeja unificada.
 *
 * ── QUE RESUELVE ────────────────────────────────────────────────────────────
 *
 * Hasta hoy, saber si un cliente contesto exigia entrar en su ficha, una por
 * una. Los canales de equipo del #2006 avisan de que alguien escribio, pero a
 * proposito NO llevan el texto (los lee toda la agencia y puede ser PHI), asi
 * que el equipo se enteraba y seguia teniendo que saltar de pantalla.
 *
 * Esto es esa pantalla: todos los hilos en una lista, el texto a la derecha y
 * una caja para contestar. Los mensajes siguen guardandose donde siempre — la
 * ficha del cliente los sigue enseñando igual.
 *
 * ── COMO SE PINTA ───────────────────────────────────────────────────────────
 *
 * Lista a la izquierda, hilo a la derecha. En movil no caben las dos, asi que
 * al abrir un hilo la lista se esconde y aparece un boton de volver: una
 * pantalla partida en un telefono no deja leer ninguna de las dos mitades.
 *
 * ── LO QUE NO SE HACE AQUI, Y POR QUE ───────────────────────────────────────
 *
 * No se filtra en el navegador. La lista se pide al servidor con sus filtros y
 * su pagina. Traersela entera para filtrar aqui es el patron que el sondeo del
 * 2026-08-30 midio haciendose seis veces mas lento al pasar de 10 personas a
 * la vez a 15 — y esta es la tabla que mas va a crecer del producto.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  MessageSquare, Mail, Send, Search, Check, ArrowLeft, User, Inbox, Loader,
} from 'lucide-react';
import {
  listConversations, getConversationMessages, replyConversation,
  markConversationRead, assignConversation, setConversationStatus,
} from '../api.js';

/** Por que no se pudo mandar, dicho como lo diria una persona. */
function motivoLegible(reason, lang) {
  const es = lang === 'es';
  switch (reason) {
    case 'no-phone':
      return es ? 'Esta ficha no tiene telefono guardado.' : 'This client has no phone on file.';
    case 'no-email':
      return es ? 'Esta ficha no tiene correo guardado.' : 'This client has no email on file.';
    case 'suppressed':
      // No es un fallo: es la lista de No-Textear haciendo su trabajo. Decirlo
      // como error empuja a buscarle la vuelta, que es justo lo que no puede
      // pasar (TCPA).
      return es
        ? 'Este numero esta en tu lista de No-Textear. El cliente pidio no recibir mensajes.'
        : 'This number is on your Do-Not-Text list. The client asked not to receive messages.';
    case 'no-integration':
    case 'no-provider':
      return es
        ? 'No tienes una linea conectada todavia. Se conecta en Ajustes -> SMS y Llamadas.'
        : 'You have no phone line connected yet. Connect one in Settings -> SMS & Calls.';
    case 'no-mailbox':
      return es
        ? 'No tienes un buzon de correo conectado. Se conecta en Mi Correo.'
        : 'You have no mailbox connected. Connect one in My Inbox.';
    case 'agent-no-mailbox':
      return es
        ? 'Los correos salen del buzon del agente, y ese buzon no esta conectado. El agente lo conecta en Mi Correo.'
        : 'Emails go out from the agent\'s mailbox, and it is not connected. The agent connects it in My Inbox.';
    default:
      return es ? 'No se pudo enviar. Intentalo de nuevo.' : 'Could not send. Try again.';
  }
}

/** "hace 5 min", "ayer", "12 sep" — lo que se lee de un vistazo en una lista. */
function cuando(iso, lang) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const es = lang === 'es';
  const minutos = Math.floor((Date.now() - d.getTime()) / 60000);
  if (minutos < 1) return es ? 'ahora' : 'now';
  if (minutos < 60) return `${minutos} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `${horas} h`;
  const dias = Math.floor(horas / 24);
  if (dias === 1) return es ? 'ayer' : 'yesterday';
  // Fechas siempre en-US y con las partes LOCALES: `toISOString` corre el dia.
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** La hora del mensaje dentro del hilo. */
function hora(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

const C = {
  borde: 'var(--c-border,#1a3550)',
  fondo: 'var(--c-surface,#0c1c2e)',
  fondo2: 'var(--c-surface2,#112538)',
  texto: 'var(--c-text,#c8dff0)',
  texto2: 'var(--c-text2,#7aa3c0)',
  texto3: 'var(--c-text3,#45688a)',
  cyan: 'var(--c-cyan,#00d4ff)',
};

export function ConversationsView({ lang = 'es', team = [], refreshSignal = 0 }) {
  const es = lang === 'es';

  const [filtro, setFiltro] = useState('all');   // all | mine | unassigned
  const [busqueda, setBusqueda] = useState('');
  const [hilos, setHilos] = useState([]);
  const [cargandoLista, setCargandoLista] = useState(true);
  const [errorLista, setErrorLista] = useState('');

  const [abierto, setAbierto] = useState(null);      // el hilo seleccionado
  const [mensajes, setMensajes] = useState([]);
  const [cargandoHilo, setCargandoHilo] = useState(false);

  const [texto, setTexto] = useState('');
  const [asunto, setAsunto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [avisoDeEnvio, setAvisoDeEnvio] = useState('');

  const finDelHilo = useRef(null);

  // ── La lista ─────────────────────────────────────────────────────────────
  const cargarLista = useCallback(async () => {
    setErrorLista('');
    try {
      const { conversations } = await listConversations({
        assignee: filtro === 'all' ? undefined : filtro,
        q: busqueda.trim() || undefined,
        limit: 50,
      });
      setHilos(conversations);
    } catch (err) {
      // El frontend se publica unos MINUTOS antes que el backend. Entre medias
      // esta pantalla pide una ruta que el servidor todavia no tiene, y un 404
      // no puede dejar la pantalla rota: se dice y se reintenta al refrescar.
      setErrorLista(es ? 'No se pudo cargar la bandeja.' : 'Could not load the inbox.');
      setHilos([]);
      if (typeof console !== 'undefined') console.warn('listConversations', err?.message);
    } finally {
      setCargandoLista(false);
    }
  }, [filtro, busqueda, es]);

  // La busqueda espera a que se deje de escribir: una peticion por tecla contra
  // la tabla mas grande del producto es exactamente lo que no se quiere.
  useEffect(() => {
    const id = setTimeout(cargarLista, busqueda ? 350 : 0);
    return () => clearTimeout(id);
  }, [cargarLista, busqueda]);

  // Un mensaje nuevo llega por SSE y `refreshSignal` cambia: se refresca la
  // lista (y el hilo abierto, si lo hay) sin que nadie recargue nada.
  useEffect(() => {
    if (!refreshSignal) return;
    cargarLista();
    if (abierto?.id) abrirHilo(abierto, { silencioso: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  // ── Un hilo ──────────────────────────────────────────────────────────────
  async function abrirHilo(hilo, { silencioso = false } = {}) {
    if (!silencioso) {
      setAbierto(hilo);
      setMensajes([]);
      setTexto('');
      setAsunto('');
      setAvisoDeEnvio('');
      setCargandoHilo(true);
    }
    try {
      const data = await getConversationMessages(hilo.id);
      setMensajes(data.messages || []);
      if (!silencioso && hilo.unreadCount > 0) {
        await markConversationRead(hilo.id).catch(() => {});
        setHilos((prev) => prev.map((h) => (h.id === hilo.id ? { ...h, unreadCount: 0 } : h)));
      }
    } catch (err) {
      if (typeof console !== 'undefined') console.warn('getConversationMessages', err?.message);
    } finally {
      setCargandoHilo(false);
    }
  }

  useEffect(() => {
    if (finDelHilo.current) finDelHilo.current.scrollIntoView({ block: 'end' });
  }, [mensajes]);

  async function contestar() {
    const cuerpo = texto.trim();
    if (!cuerpo || !abierto || enviando) return;
    setEnviando(true);
    setAvisoDeEnvio('');
    try {
      const r = await replyConversation(abierto.id, {
        body: cuerpo,
        subject: abierto.channel === 'email' ? (asunto.trim() || undefined) : undefined,
      });
      if (!r.ok) {
        setAvisoDeEnvio(motivoLegible(r.reason, lang));
        return;
      }
      setTexto('');
      // Se vuelve a pedir el hilo en vez de añadir el mensaje a mano: asi lo
      // que se ve es lo que se guardo de verdad, con su hora del servidor.
      await abrirHilo(abierto, { silencioso: true });
      cargarLista();
    } catch (err) {
      setAvisoDeEnvio(motivoLegible('', lang));
      if (typeof console !== 'undefined') console.warn('replyConversation', err?.message);
    } finally {
      setEnviando(false);
    }
  }

  async function cambiarResponsable(assigneeId) {
    if (!abierto) return;
    try {
      await assignConversation(abierto.id, assigneeId);
      setAbierto((a) => ({ ...a, assigneeId: assigneeId || null }));
      cargarLista();
    } catch (err) {
      if (typeof console !== 'undefined') console.warn('assignConversation', err?.message);
    }
  }

  async function cerrarHilo() {
    if (!abierto) return;
    try {
      await setConversationStatus(abierto.id, 'closed');
      setAbierto(null);
      cargarLista();
    } catch (err) {
      if (typeof console !== 'undefined') console.warn('setConversationStatus', err?.message);
    }
  }

  // ── Pintar ───────────────────────────────────────────────────────────────
  const FILTROS = [
    ['all', es ? 'Todas' : 'All'],
    ['mine', es ? 'Mias' : 'Mine'],
    ['unassigned', es ? 'Sin asignar' : 'Unassigned'],
  ];

  // Quien decide si se ven las dos mitades o una sola es el CSS (dkt.css), no
  // este componente: es una media query, y eso no cabe en un `style` de React.
  return (
    <div className={`dkt-conv${abierto ? ' dkt-conv-abierta' : ''}`}>
      {/* ── Izquierda: la lista ─────────────────────────────────────── */}
      <div
        className="dkt-conv-lista"
        style={{ border: `1px solid ${C.borde}`, borderRadius: 12, background: C.fondo }}
      >
        <div style={{ padding: '.6rem', borderBottom: `1px solid ${C.borde}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.35rem', marginBottom: '.5rem' }}>
            <Search size={13} color={C.texto3} />
            <input
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder={es ? 'Buscar por nombre...' : 'Search by name...'}
              style={{
                flex: 1, background: C.fondo2, border: `1px solid ${C.borde}`, borderRadius: 8,
                padding: '.4rem .5rem', color: C.texto, fontSize: '.75rem', fontFamily: 'inherit',
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: '.25rem' }}>
            {FILTROS.map(([id, etiqueta]) => (
              <button
                key={id}
                type="button"
                onClick={() => setFiltro(id)}
                className="dkt-btn xs"
                style={filtro === id
                  ? { background: C.cyan, color: '#04121f', border: 'none', fontWeight: 700 }
                  : undefined}
              >
                {etiqueta}
              </button>
            ))}
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {cargandoLista && (
            <div style={{ padding: '1.5rem', textAlign: 'center', color: C.texto3, fontSize: '.75rem' }}>
              <Loader size={16} className="dkt-spin" />
            </div>
          )}

          {!cargandoLista && errorLista && (
            <div style={{ padding: '1.5rem', textAlign: 'center', color: C.texto2, fontSize: '.75rem' }}>
              {errorLista}
              <div style={{ marginTop: '.6rem' }}>
                <button type="button" className="dkt-btn xs" onClick={cargarLista}>
                  {es ? 'Reintentar' : 'Retry'}
                </button>
              </div>
            </div>
          )}

          {!cargandoLista && !errorLista && hilos.length === 0 && (
            <div style={{ padding: '2rem 1rem', textAlign: 'center', color: C.texto3 }}>
              <Inbox size={26} style={{ opacity: .5, marginBottom: '.5rem' }} />
              <div style={{ fontSize: '.78rem', fontWeight: 600, color: C.texto2 }}>
                {es ? 'Todo al dia' : 'All caught up'}
              </div>
              <div style={{ fontSize: '.68rem', marginTop: '.3rem', lineHeight: 1.5 }}>
                {es
                  ? 'Aqui apareceran los SMS y correos de tus clientes en cuanto escriban.'
                  : 'Your clients’ texts and emails will show up here as soon as they write.'}
              </div>
            </div>
          )}

          {hilos.map((h) => {
            const sinLeer = h.unreadCount > 0;
            return (
              <button
                key={h.id}
                type="button"
                onClick={() => abrirHilo(h)}
                style={{
                  width: '100%', textAlign: 'left', display: 'block', cursor: 'pointer',
                  background: abierto?.id === h.id ? C.fondo2 : 'transparent',
                  border: 'none', borderBottom: `1px solid ${C.borde}`,
                  padding: '.55rem .6rem', fontFamily: 'inherit', color: C.texto,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '.35rem' }}>
                  {h.channel === 'sms'
                    ? <MessageSquare size={12} color={C.texto3} />
                    : <Mail size={12} color={C.texto3} />}
                  <span style={{
                    flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    fontSize: '.76rem', fontWeight: sinLeer ? 800 : 600,
                  }}>
                    {h.clientName || (es ? 'Sin nombre' : 'No name')}
                  </span>
                  <span style={{ fontSize: '.62rem', color: C.texto3, flexShrink: 0 }}>
                    {cuando(h.lastMessageAt, lang)}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '.35rem', marginTop: '.2rem' }}>
                  <span style={{
                    flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    fontSize: '.68rem', color: sinLeer ? C.texto : C.texto3,
                  }}>
                    {h.lastDirection === 'outbound' && <span style={{ color: C.texto3 }}>{es ? 'Tu: ' : 'You: '}</span>}
                    {h.preview || (es ? '(sin texto)' : '(no text)')}
                  </span>
                  {sinLeer && (
                    <span style={{
                      flexShrink: 0, background: C.cyan, color: '#04121f', borderRadius: 999,
                      padding: '0 .35rem', fontSize: '.6rem', fontWeight: 800, lineHeight: '1rem',
                    }}>
                      {h.unreadCount}
                    </span>
                  )}
                </div>
                {h.assigneeName && (
                  <div style={{ fontSize: '.6rem', color: C.texto3, marginTop: '.15rem' }}>
                    <User size={9} style={{ verticalAlign: '-1px' }} /> {h.assigneeName}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Derecha: el hilo ────────────────────────────────────────── */}
      <div
        className="dkt-conv-hilo"
        style={{ border: `1px solid ${C.borde}`, borderRadius: 12, background: C.fondo }}
      >
        {!abierto && (
          <div style={{
            flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', color: C.texto3, padding: '2rem', textAlign: 'center',
          }}>
            <MessageSquare size={30} style={{ opacity: .4, marginBottom: '.6rem' }} />
            <div style={{ fontSize: '.8rem' }}>
              {es ? 'Elige una conversacion para leerla.' : 'Pick a conversation to read it.'}
            </div>
          </div>
        )}

        {abierto && (
          <>
            <div style={{
              padding: '.55rem .7rem', borderBottom: `1px solid ${C.borde}`,
              display: 'flex', alignItems: 'center', gap: '.5rem', flexWrap: 'wrap',
            }}>
              <button type="button" className="dkt-btn xs" onClick={() => setAbierto(null)}>
                <ArrowLeft size={12} />{es ? 'Volver' : 'Back'}
              </button>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: '.82rem', fontWeight: 700, overflow: 'hidden',
                  textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {abierto.clientName}
                </div>
                <div style={{ fontSize: '.64rem', color: C.texto3 }}>
                  {abierto.channel === 'sms' ? 'SMS' : es ? 'Correo' : 'Email'}
                </div>
              </div>

              {/* Repartir. Si no llega la lista del equipo, no se enseña: un
                  selector vacio no se puede usar y solo confunde. */}
              {team.length > 0 && (
                <select
                  value={abierto.assigneeId || ''}
                  onChange={(e) => cambiarResponsable(e.target.value || null)}
                  style={{
                    background: C.fondo2, border: `1px solid ${C.borde}`, borderRadius: 8,
                    padding: '.3rem .4rem', color: C.texto, fontSize: '.7rem', fontFamily: 'inherit',
                  }}
                >
                  <option value="">{es ? 'Sin asignar' : 'Unassigned'}</option>
                  {team.map((p) => (
                    <option key={p.id} value={p.id}>{p.name || p.displayName || p.email}</option>
                  ))}
                </select>
              )}

              <button type="button" className="dkt-btn xs" onClick={cerrarHilo}>
                <Check size={12} />{es ? 'Cerrar' : 'Close'}
              </button>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '.7rem', display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
              {cargandoHilo && (
                <div style={{ textAlign: 'center', color: C.texto3, padding: '1rem' }}>
                  <Loader size={16} className="dkt-spin" />
                </div>
              )}
              {!cargandoHilo && mensajes.length === 0 && (
                <div style={{ textAlign: 'center', color: C.texto3, fontSize: '.75rem', padding: '1rem' }}>
                  {es ? 'Todavia no hay mensajes en este hilo.' : 'No messages in this thread yet.'}
                </div>
              )}
              {mensajes.map((m) => {
                const mio = m.direction === 'outbound';
                return (
                  <div key={m.id} style={{ alignSelf: mio ? 'flex-end' : 'flex-start', maxWidth: '80%' }}>
                    <div style={{
                      background: mio ? 'rgba(0,153,204,.18)' : C.fondo2,
                      border: `1px solid ${mio ? 'rgba(0,212,255,.28)' : C.borde}`,
                      borderRadius: 10, padding: '.45rem .6rem',
                      fontSize: '.76rem', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    }}>
                      {m.subject && (
                        <div style={{ fontWeight: 700, marginBottom: '.2rem', fontSize: '.72rem' }}>{m.subject}</div>
                      )}
                      {m.body || <span style={{ color: C.texto3 }}>{es ? '(sin texto)' : '(no text)'}</span>}
                    </div>
                    <div style={{ fontSize: '.6rem', color: C.texto3, marginTop: '.15rem', textAlign: mio ? 'right' : 'left' }}>
                      {hora(m.createdAt)}
                    </div>
                  </div>
                );
              })}
              <div ref={finDelHilo} />
            </div>

            <div style={{ borderTop: `1px solid ${C.borde}`, padding: '.55rem .7rem' }}>
              {abierto.channel === 'email' && (
                <input
                  value={asunto}
                  onChange={(e) => setAsunto(e.target.value)}
                  placeholder={es ? 'Asunto (opcional)' : 'Subject (optional)'}
                  style={{
                    width: '100%', background: C.fondo2, border: `1px solid ${C.borde}`, borderRadius: 8,
                    padding: '.35rem .5rem', color: C.texto, fontSize: '.72rem',
                    fontFamily: 'inherit', marginBottom: '.35rem',
                  }}
                />
              )}
              <div style={{ display: 'flex', gap: '.4rem', alignItems: 'flex-end' }}>
                <textarea
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  rows={2}
                  placeholder={es ? 'Escribe tu respuesta...' : 'Write your reply...'}
                  style={{
                    flex: 1, background: C.fondo2, border: `1px solid ${C.borde}`, borderRadius: 8,
                    padding: '.45rem .55rem', color: C.texto, fontSize: '.76rem',
                    fontFamily: 'inherit', resize: 'vertical', lineHeight: 1.5,
                  }}
                />
                <button
                  type="button"
                  className="dkt-btn primary"
                  onClick={contestar}
                  disabled={enviando || !texto.trim()}
                >
                  {enviando ? <Loader size={13} className="dkt-spin" /> : <Send size={13} />}
                  {es ? 'Enviar' : 'Send'}
                </button>
              </div>

              {/* El costo del SMS, dicho donde se decide mandarlo. El nombre del
                  proveedor no se escribe a mano: si no se sabe cual es, se dice
                  "tu linea" y ya (una lista de proveedores escrita a mano ya
                  mordio antes, E-127). */}
              {abierto.channel === 'sms' && (
                <div style={{ fontSize: '.62rem', color: C.texto3, marginTop: '.35rem' }}>
                  {es
                    ? '\u{1F4AC} Sale por tu linea · ≈1 centavo por mensaje · queda guardado en la ficha y respeta tu lista de No-Textear'
                    : '\u{1F4AC} Sent from your line · ≈1 cent per message · saved on the client record and respects your Do-Not-Text list'}
                </div>
              )}

              {avisoDeEnvio && (
                <div style={{
                  marginTop: '.4rem', fontSize: '.7rem', lineHeight: 1.5,
                  color: '#ffb700', background: 'rgba(255,183,0,.1)',
                  border: '1px solid rgba(255,183,0,.3)', borderRadius: 8, padding: '.4rem .5rem',
                }}>
                  {avisoDeEnvio}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default ConversationsView;
